import { useStore } from '@/store';
import { DirtyType } from '@/types';
import type {
  RuntimeSchemaConfig,
  RuntimeSchemaConfigOption,
  RuntimeSchemaConfigSection,
} from '@morph-mapper/schema-serialization';
import { deserializeSchemaConfig } from '@morph-mapper/schema-serialization';
import { Input } from '@morph-mapper/types';
import { getEntries, getKeys } from '@morph-mapper/utils';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';

const emptyConfigurationSection: RuntimeSchemaConfigSection = {
  title: '',
  description: '',
  options: {},
  mutate: (value) => value,
};

const emptyConfigurationOption: RuntimeSchemaConfigOption = {
  title: '',
  description: '',
  type: {} as RuntimeSchemaConfigOption['type'],
  input: Input.Text,
};

export const useSchemaConfig = () => {
  const [
    setValue,
    setIsDocModalOpen,
    preconfig,
    updatePreConfig,
    configSnapshot,
    clearDirty,
    textContent,
  ] = useStore(({ entries: e, config: c }) => [
    e.setTemplateValue,
    c.setIsDocModalOpen,
    c.getPreconfiguration(),
    c.updatePreconfiguration,
    c.getConfigSnapshot(),
    e.clearDirty,
    c.textContent,
  ]);
  const schemaConfig = useMemo(() => {
    return deserializeSchemaConfig(configSnapshot);
  }, [configSnapshot]);

  useEffect(() => {
    if (!schemaConfig) return;
    if (getIdsForConfiguration().length === 0) return;

    setDefaultIfUndefined();
  }, [preconfig, schemaConfig, textContent]);

  const setDefaultIfUndefined = <T extends keyof RuntimeSchemaConfig>() => {
    const configurations = getIdsForConfiguration()
      .map((id) => [id, getKeyById(id)])
      .map<[string, RuntimeSchemaConfig[T]]>(([id, key]) => [
        id,
        getConfigurationByKey(key),
      ]);

    // TODO: This is a hard to reason about side-effect. Should be removed:
    // https://github.com/exomodal/MorphMapper/issues/682
    if (!textContent) {
      clearDirty(DirtyType.Template);
    }

    const batchUpdates: Record<
      string,
      { key: string; options: Record<string, unknown> }
    > = {};

    configurations.forEach(([id, config]) => {
      const currentOptions = preconfig[id]?.options ?? {};
      const newOptions: Record<string, unknown> = { ...currentOptions };
      let hasMissingDefaults = false;

      (getKeys(config.options) as string[]).forEach((optionKey) => {
        const option = config.options[optionKey];
        const isDefined = getConfigValue(id, optionKey) !== undefined;

        if (!isDefined) {
          newOptions[optionKey] = option.default;
          hasMissingDefaults = true;
        }
      });

      if (!hasMissingDefaults) {
        return;
      }

      batchUpdates[id] = {
        key: getKeyById(id),
        options: newOptions,
      };
    });

    // Batch updates for each configuration ID to avoid race conditions
    getKeys(batchUpdates).forEach((id) => {
      const { key, options } = batchUpdates[id];
      updatePreConfig(id, { key, options });

      setValue(id, getConfigurationByKey(key).mutate(options));
    });
  };

  const getIdsForConfiguration = () => {
    return Object.keys(preconfig);
  };

  const getKeyById = (id: string) => {
    return preconfig[id].key;
  };

  const getConfigValue = (id: string, optionKey: string) => {
    return preconfig[id].options[optionKey];
  };

  const getOptionValue = (id: string, optionKey: string): any => {
    const config = preconfig[id];
    if (!config) {
      throw `Configuration with id '${id}' not found`;
    }

    return config.options[optionKey];
  };

  const getDefaultByKey = (id: string, optionKey: string) => {
    const configKey = getKeyById(id);
    const optionDefault =
      getConfigurationByKey(configKey).options[optionKey]?.default;

    return optionDefault;
  };

  const updateConfig = (id: string, optionKey: string, value: any) => {
    const key = getKeyById(id);
    const newOptions = { ...preconfig[id].options, [optionKey]: value };

    // TODO: we maintain the state in 2 places in the store, but check if we can do it in one place, as the state may become inconsistent.
    // Or, we assume all variables controlled by the configuration are internals and thus we don't need to maintain them in 1 of the 2 places.
    updatePreConfig(id, {
      key,
      options: newOptions,
    });

    setValue(id, getConfigurationByKey(key).mutate(newOptions));
  };

  const getConfigurationByKey = <T extends keyof RuntimeSchemaConfig>(
    key: T,
  ): RuntimeSchemaConfig[T] => {
    if (schemaConfig.isErr()) return emptyConfigurationSection;
    return schemaConfig.value?.[key];
  };

  const getOptionByKey = (configId: string, optionKey: string) => {
    const configKey = getKeyById(configId);
    const options =
      getConfigurationByKey(configKey).options[optionKey] ??
      emptyConfigurationOption;

    return options;
  };

  const hasConfiguration = () => {
    return Object.keys(preconfig).length !== 0;
  };

  const hasFilledRequiredOptions = () => {
    let inValid = false;

    getEntries(preconfig).forEach(([id, { key, options }]) => {
      const config = getConfigurationByKey(key);

      getKeys(config.options).forEach((optionKey) => {
        const isRequired = config.options[optionKey].options?.required ?? false;
        const isDefined = options[optionKey] !== undefined;

        if (isRequired && !isDefined) {
          inValid = true;
        }
      });
    });

    return inValid;
  };

  const translateConfiguration = (
    configurations: RuntimeSchemaConfig,
  ): RuntimeSchemaConfig => {
    return Object.entries(configurations).reduce((acc, [key, config]) => {
      const titleKey = `configurations.${key}.title`;
      const descriptionKey = `configurations.${key}.description`;

      const translatedConfig = {
        ...config,
        title: t(titleKey, config.title),
        description: t(descriptionKey, config.description || ''),
      };

      return {
        ...acc,
        [key]: translatedConfig,
      };
    }, {} as RuntimeSchemaConfig);
  };

  return {
    getIdsForConfiguration,
    getKeyById,
    getConfigValue,
    updateConfig,
    hasConfiguration,
    getConfigurationByKey,
    hasFilledRequiredOptions,
    setIsDocModalOpen,
    getOptionByKey,
    getOptionValue,
    getDefaultByKey,
    translateConfiguration,
  };
};
